import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import "./DeleteAvailabilityModal.css";
import axios from "axios";
import { toast } from "react-toastify";

const DeleteAvailabilityModal = ({
  onClose,
  deleteEventData,
  authToken,
  providerId,
  fetchAvailability,
  calendarRef,
  overlayClassName = "",
}) => {
  const {
    id: availabilityId,
    seriesId,
    startDate,
  } = deleteEventData || {};

  const [confirmationType, setConfirmationType] = useState(null);
  const [showconfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!seriesId) {
      setConfirmationType("single");
      setShowConfirmationModal(true);
    } else {
      setConfirmationType(null);
      setShowConfirmationModal(false);
    }
  }, [seriesId]);

  const authHeaders = { Authorization: `Bearer ${authToken}` };

  const finishSuccess = async (message) => {
    toast.success(message);
    await fetchAvailability();
    if (calendarRef?.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
    setConfirmationType(null);
    onClose();
  };

  const finishError = (error, fallback) => {
    console.error(fallback, error);
    toast.error(error.response?.data?.message || fallback);
  };

  const handleDeleteAvailability = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/availability/${availabilityId}`,
        {
          headers: authHeaders,
          params: providerId ? { providerId } : undefined,
        },
      );

      if (response.data.success) {
        await finishSuccess(
          seriesId
            ? "This availability block was deleted. The rest of the series is unchanged."
            : "This availability block was deleted.",
        );
      } else {
        toast.error(response.data.message || "Failed to delete this availability");
      }
    } catch (error) {
      finishError(error, "Failed to delete this availability");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRecurringAvailability = async () => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/availability/recurring/${seriesId}`,
        {
          headers: authHeaders,
          params: providerId ? { providerId } : undefined,
        },
      );

      if (response.data.success) {
        await finishSuccess("The entire recurring series was deleted.");
      } else {
        toast.error(
          response.data.message || "Failed to delete entire recurring series",
        );
      }
    } catch (error) {
      finishError(error, "Failed to delete entire recurring series");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFutureRecurringAvailability = async () => {
    try {
      const dateParam = String(startDate || "").slice(0, 10);
      if (!dateParam) {
        toast.error("Start date is required to delete future instances.");
        setIsLoading(false);
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/availability/future-recurring/${seriesId}`,
        {
          headers: authHeaders,
          params: {
            date: dateParam,
            ...(providerId ? { providerId } : {}),
          },
        },
      );

      if (response.data.success) {
        await finishSuccess(
          "All future instances in this series were deleted.",
        );
      } else {
        toast.error(
          response.data.message || "Failed to delete future recurring series",
        );
      }
    } catch (error) {
      finishError(error, "Failed to delete future recurring series");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setConfirmationType(null);
    setShowConfirmationModal(false);
    onClose();
  };

  const confirmDeleteWhileEdit = () => {
    if (isLoading) return;
    setIsLoading(true);
    if (confirmationType === "single") {
      handleDeleteAvailability();
    } else if (confirmationType === "all") {
      handleDeleteRecurringAvailability();
    } else if (confirmationType === "future") {
      handleDeleteFutureRecurringAvailability();
    } else {
      setIsLoading(false);
    }
  };

  return (
    <div className={`modal-overlay ${overlayClassName}`.trim()}>
      <div className="modal-container delete-modal">
        <div className="modal-header">
          <div className="heading">Delete Availability</div>
          <button className="close-button" onClick={handleCancel}>
            <FaTimes />
          </button>
        </div>

        {showconfirmationModal && confirmationType ? (
          <div className="modal-body">
            <h5>
              {confirmationType === "single"
                ? "Delete this availability block?"
                : confirmationType === "future"
                  ? "Delete all future instances in this series?"
                  : "Delete the entire recurring series?"}
            </h5>
            <div className="form-actions">
              <button
                className="cancel-button"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                className="delete-button"
                onClick={confirmDeleteWhileEdit}
                disabled={isLoading}
              >
                {!isLoading ? "Delete" : "Deleting.."}
              </button>
            </div>
          </div>
        ) : (
          <div className="modal-body text-start">
            <p className="description">
              This availability is part of a recurring series. What would you
              like to delete?
            </p>
            <form className="delete-options d-grid">
              <label className="d-flex gap-2">
                <input
                  type="radio"
                  value="single"
                  checked={confirmationType === "single"}
                  onChange={() => setConfirmationType("single")}
                />
                Delete This Only
              </label>

              <label className="d-flex gap-2">
                <input
                  type="radio"
                  value="future"
                  checked={confirmationType === "future"}
                  onChange={() => setConfirmationType("future")}
                />
                Delete All Future Instances
              </label>

              <label className="d-flex gap-2">
                <input
                  type="radio"
                  value="all"
                  checked={confirmationType === "all"}
                  onChange={() => setConfirmationType("all")}
                />
                Delete Entire Series
              </label>
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="delete-button"
                  onClick={() => setShowConfirmationModal(true)}
                  disabled={!confirmationType || isLoading}
                >
                  {!isLoading ? "Delete" : "Deleting.."}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteAvailabilityModal;
